// VG2 · VALUTA · Approfondimento economico di UN singolo scenario.
//
// Si apre in una nuova scheda dal badge di uno scenario nelle schede
// VALUTA. Mostra numeri, stime e indicatori economici dello scenario
// selezionato RIUSANDO i dati già calcolati (decision engine) + la
// valutazione di base: nessuna ricompilazione, nessuna chiamata AI,
// nessun numero inventato. Ogni stima è accompagnata dai dati e dalle
// ipotesi da cui nasce; se un dato manca, viene mostrato "non disponibile".
import React from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import {
  EconomicBlock,
  buildVerdictPhrase,
  _SEM_BADGE as SEM_BADGE,
} from "./ValutaResult";
import SaveAnalysisButton from "@/components/vg2/SaveAnalysisButton";

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

function authHeaders() {
  const h = {};
  const owner = localStorage.getItem("vg2_token");
  const anon = localStorage.getItem("vg2_anon_token");
  if (owner) h.Authorization = `Bearer ${owner}`;
  if (anon) h["X-VG2-Anon-Token"] = anon;
  return h;
}

const fmtEur = (v) =>
  v == null ? null : new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
    useGrouping: "always",
  }).format(v);

const FEATURE_LABELS = {
  surface_sqm: "Superficie (m²)",
  condition: "Stato di conservazione",
  rooms: "Locali",
  bedrooms: "Camere da letto",
  bathrooms: "Bagni",
  floor: "Piano",
  floors_total: "Piani edificio",
  elevator: "Ascensore",
  balcony: "Balcone",
  terrace: "Terrazzo",
  garden: "Giardino",
  parking: "Posto auto",
  box: "Box auto",
  cellar: "Cantina",
  heating: "Riscaldamento",
  exposure: "Esposizione",
  brightness: "Luminosità",
  view: "Vista",
  occupancy: "Occupazione",
  energy_class: "Classe energetica",
  year_built: "Anno di costruzione",
  zone: "Zona / microzona",
};

// §CEO-PRES-UI · Localizza in italiano il VALORE della caratteristica.
// Mappa PER CAMPO: lo stesso token può significare cose diverse (es.
// `intermediate` = "Piano intermedio" per `floor`, mai "da rimodernare").
// I valori corrispondono agli enum del questionario VALUTA (backend).
const FEATURE_VALUE_LABELS = {
  condition: { excellent: "Ottimo", good: "Buono", to_renovate: "Da ristrutturare" },
  floor: {
    basement: "Seminterrato", ground: "Piano terra", first: "Primo piano",
    intermediate: "Piano intermedio", top: "Ultimo piano / attico",
    multi_level: "Su più livelli",
  },
  exposure: {
    north: "Nord", south: "Sud", east: "Est", west: "Ovest",
    double: "Doppia esposizione", unknown: "Non indicata",
  },
  brightness: { high: "Molto luminoso", medium: "Nella media", low: "Poco luminoso", unknown: "Non indicata" },
  view: { open: "Aperta / panoramica", standard: "Normale", internal: "Interna / su cortile", unknown: "Non indicata" },
  occupancy: {
    free: "Libero", owner_occupied: "Occupato dal proprietario",
    rented: "Locato a terzi", other: "Altra situazione",
  },
};

function localizeFeatureValue(name, value) {
  if (value === true) return "Sì";
  if (value === false) return "No";
  const key = String(value).trim().toLowerCase();
  const perField = FEATURE_VALUE_LABELS[name];
  if (perField && perField[key]) return perField[key];
  if (key === "true" || key === "yes") return "Sì";
  if (key === "false" || key === "no") return "No";
  if (key === "unknown") return "Non indicata";
  if (name === "surface_sqm") return `${value} m²`;
  return String(value);
}

function addressLine(a) {
  if (!a) return null;
  // Evita il civico duplicato quando ``street`` lo contiene già.
  const streetHasNum = a.street && a.house_number
    && new RegExp(`\\b${String(a.house_number).trim()}\\b`).test(a.street);
  const parts = [a.street, streetHasNum ? null : a.house_number, a.municipality]
    .filter(Boolean);
  return parts.length ? parts.join(" ") : (a.municipality || null);
}

function Fact({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-vg2-line/50"
         data-testid={testid}>
      <dt className="text-sm text-vg2-muted">{label}</dt>
      <dd className="text-sm font-medium text-vg2-ink text-right">
        {value == null || value === "" ? (
          <span className="text-vg2-muted italic">non disponibile</span>
        ) : value}
      </dd>
    </div>
  );
}

const fmtRange = (r, suffix) =>
  (!r || r.low == null || r.high == null)
    ? null
    : `${fmtEur(r.low)} – ${fmtEur(r.high)}${suffix || ""}`;

// §VALUTA-PROJECT · ponte economico del progetto scritto dell'utente:
// reddito netto casa vacanze → costo per affittare una casa più grande in
// zona → margine residuo. Numeri già calcolati, ipotesi esplicite.
function ProjectFitBlock({ fit }) {
  const coverage = fit.coverage || (fit.covers_larger_home ? "covers" : "shortfall");
  const verdictText = {
    covers:
      "Nella fascia stimata, il reddito della casa vacanze può coprire "
      + "l’affitto di un’abitazione più grande in zona, lasciando un "
      + "margine di liquidità positivo.",
    depends:
      "Nella fascia stimata, la copertura dell’affitto di un’abitazione più "
      + "grande dipende dalle ipotesi: nello scenario favorevole resta un "
      + "margine positivo, in quello prudente il reddito potrebbe non bastare.",
    shortfall:
      "Nella fascia stimata, il reddito della casa vacanze potrebbe non "
      + "bastare a coprire del tutto l’affitto di un’abitazione più grande "
      + "in zona: il margine può risultare negativo.",
  }[coverage];
  // §CEO-OBJ1 · preferisci il verdetto robusto dal backend (dice se il
  // progetto si autofinanzia anche all'estremo ottimistico), più chiaro e
  // decision-utile della frase generica per fascia.
  const verdictFinal = fit.verdict_detail || verdictText;
  const verdictClass = coverage === "shortfall" ? "text-vg2-error" : "text-vg2-ink";
  return (
    <section className="mb-8" data-testid="valuta-deepdive-project-fit">
      <h2 className="font-display text-xl text-vg2-ink mb-2">
        Il valore rispetto al tuo progetto
      </h2>
      <p className="text-sm text-vg2-muted mb-3 leading-relaxed">
        Mettiamo in relazione il reddito netto della casa vacanze con il
        costo per affittare un’abitazione più grande nella stessa zona, e
        quanto resta come margine di liquidità.
      </p>
      <dl className="rounded-xl border border-vg2-line p-4 mb-3">
        <Fact label="Reddito netto casa vacanze (annuo)"
              value={fmtRange(fit.casa_vacanze_net_annual)}
              testid="project-fit-net" />
        <Fact label={`Canone casa più grande in zona · ~${fit.larger_home_sqm} m² (annuo)`}
              value={fmtRange(fit.larger_home_rent_annual)}
              testid="project-fit-larger-rent" />
        <Fact label="Margine residuo (annuo)"
              value={fmtRange(fit.residual_margin_annual)}
              testid="project-fit-residual-annual" />
        <Fact label="Margine residuo (mensile)"
              value={fmtRange(fit.residual_margin_monthly, " /mese")}
              testid="project-fit-residual-monthly" />
      </dl>
      <p className={"text-sm font-medium mb-3 " + verdictClass}
         data-testid="project-fit-verdict">
        {verdictFinal}
      </p>
      {fit.assumptions?.length > 0 && (
        <div className="rounded-xl border border-vg2-line p-4 mb-3"
             data-testid="project-fit-assumptions">
          <p className="text-xs uppercase tracking-wider text-vg2-muted mb-2">
            Ipotesi e fasce di incertezza
          </p>
          <ul className="space-y-1 list-disc list-inside">
            {fit.assumptions.map((a, i) => (
              <li key={i} className="text-sm text-vg2-ink">{a}</li>
            ))}
          </ul>
        </div>
      )}
      {fit.disclaimer && (
        <p className="text-xs text-vg2-muted italic leading-relaxed"
           data-testid="project-fit-disclaimer">
          {fit.disclaimer}
        </p>
      )}
    </section>
  );
}


export default function ValutaScenarioDeepDive() {
  const { caseId, analysisId, scenarioKey } = useParams();
  const [state, setState] = React.useState({ loading: true, error: null, card: null, result: null, address: null, asterisk: null });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [decision, valuation] = await Promise.all([
          axios.get(`${API_V2}/cases/${caseId}/decision`, { headers: authHeaders() }).then((r) => r.data),
          axios.get(`${API_V2}/cases/${caseId}/valuations/${analysisId}`, { headers: authHeaders() }).then((r) => r.data).catch(() => null),
        ]);
        if (cancelled) return;
        const scenarios = decision?.scenarios || [];
        const card = scenarios.find(
          (s) => String(s.scenario_key).toUpperCase() === String(scenarioKey).toUpperCase());
        if (!card) {
          setState({ loading: false, error: "Scenario non trovato in questa analisi.", card: null });
          return;
        }
        // §VALUTA-PROJECT · ponte economico del progetto (casa vacanze →
        // casa più grande → margine). Solo per lo scenario casa vacanze.
        let projectFit = null;
        if (String(card.scenario_key).toLowerCase() === "short_term_rent") {
          projectFit = await axios
            .get(`${API_V2}/cases/${caseId}/decision/project-fit`, { headers: authHeaders() })
            .then((r) => r.data).catch(() => null);
          if (cancelled) return;
        }
        const result = valuation?.valuation_result || valuation?.result || {};
        const prop = valuation?.inputs_snapshot?.property?.address || null;
        const address = prop
          ? { street: prop.street || prop.raw, house_number: prop.street_number, municipality: prop.municipality }
          : (valuation?.property?.address || valuation?.address || result?.address || null);
        setState({
          loading: false, error: null, card, result, address,
          asterisk: decision?.universal_asterisk_note || null,
          projectFit,
        });
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.status === 404
            ? "Analisi non trovata o non accessibile con questa sessione."
            : "Non riesco a caricare l'approfondimento.";
          setState({ loading: false, error: msg, card: null });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, analysisId, scenarioKey]);

  if (state.loading) {
    return (
      <Vg2Shell>
        <div className="pt-24 text-vg2-muted" data-testid="valuta-deepdive-loading">
          Sto preparando l'approfondimento economico…
        </div>
      </Vg2Shell>
    );
  }
  if (state.error) {
    return (
      <Vg2Shell>
        <div className="pt-24" data-testid="valuta-deepdive-error">
          <p role="alert" className="text-vg2-error">{state.error}</p>
        </div>
      </Vg2Shell>
    );
  }

  const { card, result, address, asterisk, projectFit } = state;
  const badge = SEM_BADGE[card?.semaphore?.state] || SEM_BADGE.DA_CHIARIRE;
  const phrase = buildVerdictPhrase(card);
  const econ = card.economic_analysis || null;
  const baseLow = result?.absolute_value_low;
  const baseCentral = result?.absolute_value_central;
  const baseHigh = result?.absolute_value_high;
  // §CEO-PRES · in modalità affitto la "valutazione di base" è un canone
  // MENSILE: va mostrata con l'unità /mese (prima appariva come importo
  // secco senza unità) e con un'etichetta coerente.
  const baseIsRent = String(result?.unit || "").toLowerCase().includes("month")
    || String(result?.unit || "").toLowerCase().includes("mese");
  const baseSuffix = baseIsRent ? " /mese" : "";
  const baseLabel = baseIsRent
    ? "Canone di base dell'immobile (mensile)"
    : "Valutazione di base dell'immobile";
  const baseRange = (baseLow != null && baseHigh != null)
    ? `${fmtEur(baseLow)} – ${fmtEur(baseHigh)}${baseSuffix}`
    : (baseCentral != null ? `${fmtEur(baseCentral)}${baseSuffix}` : null);
  const adjustments = result?.adjustments || [];
  // §CEO-PRES · localizzo l'enum di confidenza (prima mostrava il valore
  // grezzo inglese "MEDIUM" su UI italiana).
  const _CONF_IT = {
    HIGH: "Alta", MEDIUM: "Media", LOW: "Orientativa",
  };
  const confLevelIt = result?.confidence_level
    ? (_CONF_IT[String(result.confidence_level).toUpperCase()]
       || result.confidence_level)
    : null;

  return (
    <Vg2Shell>
      <div className="max-w-3xl mx-auto pt-20 pb-24 px-1" data-testid="valuta-deepdive">
        <p className="text-xs uppercase tracking-[0.16em] text-vg2-red mb-1">
          Approfondimento VALUTA
        </p>
        <h1 className="font-display text-3xl sm:text-4xl text-vg2-ink leading-tight mb-2"
            data-testid="valuta-deepdive-title">
          {card.title}
        </h1>
        {address && (
          <p className="text-sm text-vg2-muted mb-4" data-testid="valuta-deepdive-address">
            {addressLine(address)}
          </p>
        )}

        {/* Voto + semaforo + frase (coerenti con la scheda d'origine) */}
        <div className="flex items-start gap-4 flex-wrap mb-6 p-4 rounded-xl border border-vg2-line">
          {card.score != null && (
            <div className="text-center" data-testid="valuta-deepdive-score">
              <p className="font-display font-semibold text-3xl leading-none">
                {card.score}<span className="text-sm text-vg2-muted font-normal">/100</span>
              </p>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 mb-1" data-testid="valuta-deepdive-semaphore">
              <span className={"w-2.5 h-2.5 rounded-full " + badge.dot} aria-hidden />
              <span className={"text-xs font-medium " + badge.txt}>{badge.label}</span>
            </div>
            <p className="text-sm text-vg2-ink leading-relaxed" data-testid="valuta-deepdive-rationale">
              {phrase}
            </p>
          </div>
        </div>

        {/* Punto di partenza — la valutazione dell'immobile da cui nascono le stime */}
        <section className="mb-6" data-testid="valuta-deepdive-base">
          <h2 className="font-display text-xl text-vg2-ink mb-2">Punto di partenza</h2>
          <p className="text-sm text-vg2-muted mb-3 leading-relaxed">
            L'approfondimento parte dalla valutazione dell'immobile già
            calcolata e la declina su questo scenario.
          </p>
          <dl className="rounded-xl border border-vg2-line p-4">
            <Fact label={baseLabel} value={baseRange} testid="valuta-deepdive-base-range" />
            <Fact label="Livello di confidenza" value={confLevelIt} testid="valuta-deepdive-confidence" />
          </dl>
        </section>

        {/* Analisi economica dello scenario (riuso EconomicBlock) */}
        <section className="mb-6" data-testid="valuta-deepdive-economics-section">
          <h2 className="font-display text-xl text-vg2-ink mb-2">Analisi economica dello scenario</h2>
          {econ ? (
            <div className="rounded-xl border border-vg2-line p-4">
              <EconomicBlock econ={econ} />
            </div>
          ) : (
            <p className="text-sm text-vg2-muted italic">
              Per questo scenario non sono disponibili numeri economici calcolati.
            </p>
          )}
        </section>

        {projectFit?.applicable && (
          <ProjectFitBlock fit={projectFit} />
        )}

        {(() => {
          const cons = [
            ...(card.material_constraints || []),
            ...(card.extra_constraints || []),
          ];
          if (!cons.length) return null;
          return (
            <section className="mb-8" data-testid="valuta-deepdive-constraints">
              <h2 className="font-display text-xl text-vg2-ink mb-2">
                Vincoli e verifiche
              </h2>
              <p className="text-sm text-vg2-muted mb-3 leading-relaxed">
                Cosa va verificato o considerato prima di procedere con questa
                strategia.
              </p>
              <ul className="space-y-2 rounded-xl border border-vg2-line p-4">
                {cons.map((c, i) => (
                  <li key={i} className="text-sm text-vg2-ink flex gap-2"
                      data-testid={`valuta-deepdive-constraint-${i}`}>
                    <span aria-hidden className="text-vg2-red">•</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })()}

        {/* Dati e ipotesi — provenienza esplicita, niente numeri inventati */}
        <section className="mb-8" data-testid="valuta-deepdive-assumptions">
          <h2 className="font-display text-xl text-vg2-ink mb-2">Dati e ipotesi</h2>
          <p className="text-sm text-vg2-muted mb-3 leading-relaxed">
            Da quali dati e ipotesi nascono questi numeri.
          </p>
          {adjustments.filter((a) => a.detected_value != null).length > 0 && (
            <div className="rounded-xl border border-vg2-line p-4 mb-3" data-testid="valuta-deepdive-adjustments">
              <p className="text-xs uppercase tracking-wider text-vg2-muted mb-2">Dati dell'immobile usati</p>
              <ul className="space-y-1">
                {adjustments.filter((a) => a.detected_value != null).map((a, i) => (
                  <li key={i} className="text-sm text-vg2-ink flex items-baseline justify-between gap-3">
                    <span>{FEATURE_LABELS[a.feature_name] || a.feature_name}</span>
                    <span className="text-vg2-muted">{localizeFeatureValue(a.feature_name, a.detected_value)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {econ?.caveats?.length > 0 && (
            <div className="rounded-xl border border-vg2-line p-4 mb-3" data-testid="valuta-deepdive-caveats">
              <p className="text-xs uppercase tracking-wider text-vg2-muted mb-2">Ipotesi e cautele</p>
              <ul className="space-y-1 list-disc list-inside">
                {econ.caveats.map((c, i) => (
                  <li key={i} className="text-sm text-vg2-ink">{c}</li>
                ))}
              </ul>
            </div>
          )}
          {result?.zone_narrative && (
            <div className="rounded-xl border border-vg2-line p-4 mb-3">
              <p className="text-xs uppercase tracking-wider text-vg2-muted mb-2">Contesto di zona</p>
              <p className="text-sm text-vg2-ink leading-relaxed">{result.zone_narrative}</p>
            </div>
          )}
          <p className="text-xs text-vg2-muted italic">
            {asterisk || "Numeri indicativi, da verificare con un professionista."}
          </p>
        </section>

        {/* Salvataggio nella dashboard, mantenendo immobile + scenario d'origine */}
        <div className="flex items-center gap-3 flex-wrap border-t border-vg2-line pt-6">
          <SaveAnalysisButton
            caseId={caseId}
            analysisId={analysisId}
            scenarioKey={card.scenario_key}
            scenarioTitle={card.title}
            testid="valuta-deepdive-save"
          />
          <span className="text-xs text-vg2-muted">
            L'analisi generale resta aperta nella scheda originale.
          </span>
        </div>
      </div>
    </Vg2Shell>
  );
}
